import { mapState } from 'vuex';
import { i18n } from '@/tools/i18n';
import { logger } from '@/tools/logger';
import { sleep } from '@/tools/sleep';

function zeroPad(num, size) {
	const s = `000000000${num}`;
	return s.substr(s.length - size);
}

function returnReadableTime(timeInSeconds) {
	const seconds = timeInSeconds % 60;
	const tmp = (timeInSeconds - seconds) / 60;
	const minutes = tmp % 60;
	const hours = (tmp - minutes) / 60;
	const secondsDisplay = zeroPad(Math.round(seconds), 2);
	return `${hours > 0 ? hours + ':' : ''}${hours ? zeroPad(minutes, 2) : minutes}:${secondsDisplay}`;
}

async function waitForNativeinterface() {
	return new Promise(async resolve => {
		while (!window.nativeInterface) {
			logger.log('native Interface not up wait for it!');
			await sleep(100);
		}
		resolve();
	});
}

function isIOS() {
	return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function mapStateNested(statePath = '', props = []) {
	if (typeof statePath === 'string') {
		statePath = statePath.split('.');
	}

	function getNestedState(state) {
		return statePath.reduce((acc, field) => acc[field], state);
	}

	return mapState(
		props.reduce((acc, prop) => {
			acc[prop] = state => getNestedState(state)[prop];
			return acc;
		}, {})
	);
}

function detectLeftButton(event) {
	if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
		return false;
	} else if ('buttons' in event) {
		return event.buttons === 1;
	} else if ('which' in event) {
		return event.which === 1;
	} else {
		return event.button == 1 || event.type == 'click';
	}
}

function paddingLeft(paddingString, value) {
	return String(paddingString + value).slice(-paddingString.length);
}

function capitalizeFirstLetter(string) {
	if (!string) return string;
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function getWeekDayName(dayOfWeek) {
	return isNaN(dayOfWeek)
		? null
		: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
}

export function isValidUuid(string) {
	return /[0-9a-z]{24}/.test(string);
}

'August', 'September', 'October', 'November', 'December';
function getMonthName(month) {
	return isNaN(month)
		? null
		: [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
		  ][month];
}
/**
 * catchFocus is used to workaround restriction on ios devices setting focus via javascript
 * it is only allowed in click events but it is allowed to move the focus.
 * catchFocus in click event move focus on target input field after rendering of input field
 */
function catchFocus() {
	document.getElementById('focusCatcher').focus();
}

function toClassSave(string) {
	/* eslint-disable */
	return string.replace(/[^0-9a-zA-Z\-]+/g, '');
}

const regexp = /^[a-f\d]{24}$/i;
function isDataBaseID(value) {
	return regexp.test(value);
}

export function shallowEqual(a, b, keys) {
	if (a === b) {
		return true;
	}

	if (typeof a !== typeof b || typeof a !== 'object') {
		return false;
	}

	const aKeys = keys || Object.keys(a);
	const bKeys = keys || Object.keys(b);

	if (aKeys.length !== bKeys.length) {
		return false;
	}

	for (const key of aKeys) {
		if (a[key] !== b[key]) {
			return false;
		}
	}

	return true;
}

function generateUUID() {
	let d = Date.now();
	return 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
		const r = (d + Math.random() * 16) % 16 | 0;
		d = Math.floor(d / 16);
		return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
	});
}

function getFigcaptionLabel(post) {
	const showElements = post
		.getAttribute('elements-hidden')
		.split(',')
		.reduce((acc, part) => {
			const array = part.split(':');
			acc[array[0]] = array[1] === 'true';
			return acc;
		}, {});
	const $title = showElements['element__caption__headline']
		? post.querySelector('.element__caption__headline').textContent
		: '';
	const $description = showElements['element__caption__text']
		? post.querySelector('.element__caption__text').textContent
		: '';
	const $time = showElements['element__caption__time']
		? post.querySelector('.element__caption__time').textContent
		: '';
	const $attribution = showElements['element__caption__attribution']
		? post.querySelector('.element__caption__attribution').textContent
		: '';
	return `${$title} ${$description} ${$time ? `${i18n.t('duration')} ${$time}` : ''} ${$attribution}`;
}
function getFigcaptionTitle(post) {
	const showElements = post
		.getAttribute('elements-hidden')
		.split(',')
		.reduce((acc, part) => {
			const array = part.split(':');
			acc[array[0]] = array[1] === 'true';
			return acc;
		}, {});
	const $title = showElements['element__caption__headline']
		? post.querySelector('.element__caption__headline').textContent
		: '';
	const $time = showElements['element__caption__time']
		? post.querySelector('.element__caption__time').textContent
		: '';
	return `${$title} ${$time ? `${i18n.t('duration')} ${$time}` : ''}`;
}

function getElementOffset(el) {
	let top = 0;
	let left = 0;
	let element = el;

	// Loop through the DOM tree
	// and add it's parent's offset to get page offset
	do {
		top += element.offsetTop || 0;
		left += element.offsetLeft || 0;
		element = element.offsetParent;
	} while (element);

	return {
		top,
		left,
	};
}

export {
	toClassSave,
	catchFocus,
	capitalizeFirstLetter,
	waitForNativeinterface,
	returnReadableTime,
	isIOS,
	mapStateNested,
	detectLeftButton,
	paddingLeft,
	getWeekDayName,
	getMonthName,
	getElementOffset,
	isDataBaseID,
	generateUUID,
	getFigcaptionLabel,
	getFigcaptionTitle,
};
